import { Dispatch, SetStateAction } from 'react';
import * as R from 'ramda';

import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestsFilters } from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  convertRangeDateToMillis,
  DateRange,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import { STATUS_LABELS } from '../assetRequestConstants';

import { getADOTStatusOptions } from './adotFilterUtilities';

import classes from './adotFilters.module.css';

const { MenuItem } = Select;

const styles = {
  input: {
    marginTop: '1rem',
  },
  label: {
    color: colors.neutral.dim,
  },
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
};

type ADOTReplenishmentFiltersProps = {
  filtersCart: AssetRequestsFilters;
  setFiltersCart: Dispatch<SetStateAction<AssetRequestsFilters>>;
  createdPresetSelected?: DateRange;
  setCreatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
};

const ADOTReplenishmentFilters = ({
  filtersCart,
  setFiltersCart,
  createdPresetSelected,
  setCreatedPresetSelect,
}: ADOTReplenishmentFiltersProps) => {
  const updateFilter = (value: any, property: keyof AssetRequestsFilters) => {
    setFiltersCart(prev => ({ ...prev, [property]: value }));
  };

  const getDateValue = (property: keyof AssetRequestsFilters) => {
    if (!isNilOrEmpty(filtersCart[property])) {
      return new Date(Number(filtersCart[property]));
    }

    return null;
  };

  const handleDateChange =
    (
      property: keyof AssetRequestsFilters,
      terminator: DateRangeTerminator,
      preset?: DateRange,
    ) =>
    (val: Date) => {
      updateFilter(convertRangeDateToMillis(val, terminator, preset), property);
    };

  const statusOptions = getADOTStatusOptions(filtersCart.type);

  return (
    <>
      <div className={classes.inputContainer}>
        <TextField
          style={styles.input}
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={filtersCart.name || ''}
          placeholder="Enter number only"
          label="Request ID"
          onChange={event => updateFilter(String(event.target.value), 'name')}
          data-cy="adotRequestsFiltersRequestIdInput"
        />
      </div>
      <div className={classes.inputContainer}>
        <Select
          key="status"
          multiple
          displayEmpty
          label="Status"
          value={filtersCart?.statuses || []}
          onChange={event => updateFilter(event.target.value, 'statuses')}
          renderValue={R.length(filtersCart?.statuses) > 0 ? null : () => 'All'}
          inputLabelSx={inputLabelSx}
          data-cy="adotRequestsFiltersStatusSelect"
        >
          {statusOptions.map(status => (
            <MenuItem key={status} value={status}>
              {STATUS_LABELS[status]}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.createdBy}
          updateValue={newValue => updateFilter(newValue, 'createdBy')}
          placeholder="Search users"
          label="Created By"
          showTitle
          dataCy="adotRequestsFiltersCreatedBy"
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Created Date"
          presetSelected={createdPresetSelected}
          startDateValue={getDateValue('createdDateStart')}
          handleStartChange={handleDateChange(
            'createdDateStart',
            DateRangeTerminator.START,
            createdPresetSelected,
          )}
          endDateValue={getDateValue('createdDateEnd')}
          handleEndChange={handleDateChange(
            'createdDateEnd',
            DateRangeTerminator.END,
            createdPresetSelected,
          )}
          handleSelectDatePreset={selected =>
            setCreatedPresetSelect(selected?.value)
          }
        />
      </div>
    </>
  );
};

export default ADOTReplenishmentFilters;
